<template>
  <b-modal
    id="compose-mail"
    :visible="shallShowEmailComposeModal"
    modal-class="modal-sticky"
    footer-class="d-flex justify-content-between"
    body-class="p-0"
    size="lg"
    no-close-on-backdrop
    @change="(val) => $emit('update:shall-show-email-compose-modal', val)"
  >
    <!-- Modal Header -->
    <template #modal-header>
      <h5 class="modal-title">
        Enviar mensaje: {{ (composeData.subject) ? composeData.subject : '(sin asunto)' }}
      </h5>
      <div class="modal-actions">
        <feather-icon
          icon="MinusIcon"
          class="cursor-pointer"
          @click="$emit('update:shall-show-email-compose-modal', false)"
        />
        <feather-icon
          icon="XIcon"
          class="ml-1 cursor-pointer"
          @click="discardEmail"
        />
      </div>
    </template>

    <!-- Modal Footer -->
    <template #modal-footer>
      <!-- Footer: Left Content -->
      <div />

      <!-- Footer: Right Content -->
      <div class="text-right">
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          :disabled="loader"
          @click="sendEmail"
        >
          Enviar
        </b-button>
      </div>
    </template>

    <b-overlay
      :show="loader"
      opacity="0.50"
      rounded="sm"
    >
      <!-- Modal: Body -->
      <b-form>
        <!-- Field: To -->
        <slot name="to" />

        <!-- Field: Subject -->
        <div class="compose-mail-form-field mx-2 my-1">
          <span
            for="email-subject"
            class="font-weight-bolder"
          >Asunto: </span>
          <b-form-input
            id="email-subject"
            v-model="composeData.subject"
            class="flex-grow-1 email-to-selector"
            placeholder="Escribir asunto"
            style="border: none !important"
          />
        </div>

        <hr>

        <!-- Field: Message - Quill Editor -->
        <div class="message-editor">
          <div class="mx-2 my-1">
            <span
              for="quil-content"
              class="font-weight-bolder"
            >
              Mensaje:
            </span>
            <quill-editor
              id="quil-content"
              v-model="composeData.message"
              :options="editorOption"
            />
          </div>
          <div
            id="quill-toolbar"
            class="d-flex"
          >
            <span class="text-muted">Barra de herramientas: </span>
            <button class="ql-bold" />
            <button class="ql-italic" />
            <button class="ql-underline" />
          </div>
        </div>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BForm,
  BFormInput,
  BButton,
  BOverlay,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { ref, watch } from '@vue/composition-api'
import { quillEditor } from 'vue-quill-editor'
import { isObject, isEmptyObject } from '@core/utils/utils'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import sendMail from './useCompose'

export default {
  directives: {
    Ripple,
  },
  components: {
    BForm,
    BFormInput,
    BButton,
    BOverlay,

    // 3rd Party
    quillEditor,
  },
  model: {
    prop: 'shallShowEmailComposeModal',
    event: 'update:shall-show-email-compose-modal',
  },
  props: {
    shallShowEmailComposeModal: {
      type: Boolean,
      required: true,
    },
    composeTo: {
      type: [String, Number, null],
      default: null,
    },
    extraData: {
      type: Object,
      default: null,
    },
    dataRequest: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast()
    const loader = ref(false)
    const composeData = ref({})
    const showCcField = ref(false)
    const showBccField = ref(false)
    const editorOption = {
      modules: {
        toolbar: '#quill-toolbar',
      },
      placeholder: '',
    }

    //* Let's send email
    const sendEmail = () => {
      const toSend = props.dataRequest
      if (props.extraData && isObject(props.extraData) && !isEmptyObject(props.extraData)) toSend.extra = JSON.stringify(props.extraData)
      toSend.composeData = JSON.stringify(composeData.value)

      loader.value = true
      sendMail(toSend).then(response => {
        loader.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Envío exitoso',
            text: response.message,
            icon: 'MailIcon',
            variant: 'success',
          },
        },
        { timeout: 8000 })
        composeData.value = {}
        emit('update:shall-show-email-compose-modal', false)
      }, error => {
        loader.value = false
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error en petición [Envío de mensaje]',
            text: error,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        },
        { timeout: 10000 })
      })
    }

    //* What happend when we discard message => Clean composeData object and close modal
    const discardEmail = () => {
      composeData.value = {}
      emit('update:shall-show-email-compose-modal', false)
    }

    //* When the modal is shown, set composeData.to
    watch(() => props.shallShowEmailComposeModal, value => {
      if (value && props.composeTo) composeData.value.to = props.composeTo
    })

    return {
      composeData,
      editorOption,
      showCcField,
      showBccField,

      // Email actions
      sendEmail,
      discardEmail,

      // Loader
      loader,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/quill.scss';
</style>

<style lang="scss" scoped>
form ::v-deep {

  // Mail To vue-select style
  .v-select {
    .vs__dropdown-toggle {
      border: 0;
      box-shadow: none;
    }
    .vs__open-indicator {
      display: none;
    }
  }

  // Quill Editor Style
  .quill-editor {
    .ql-container.ql-snow {
      height: 15rem;
      border: 0 !important;
      // border-bottom: 0 !important;
    }
  }

  .quill-editor {
    .ql-container.ql-bubble {
      height: 15rem;
      border: 0 !important;
      border-radius: 0 !important;
    }
  }
}
</style>
